import * as React from "react"
import { format, subDays, startOfMonth, endOfMonth, startOfYear } from "date-fns"
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

const presets = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "This Month", value: "this_month" },
  { label: "Last Month", value: "last_month" },
  { label: "This Year", value: "this_year" },
  { label: "All Time", value: "all_time" },
]

export function DateRangePicker({ 
  dateRange, 
  onDateRangeChange,
  className 
}) {
  const [open, setOpen] = React.useState(false)

  const handlePresetClick = (preset) => {
    const today = new Date()
    let from, to
    
    if (preset.days) {
      from = subDays(today, preset.days)
      to = today
    } else if (preset.value === "this_month") {
      from = startOfMonth(today)
      to = today
    } else if (preset.value === "last_month") {
      const lastMonth = subDays(startOfMonth(today), 1)
      from = startOfMonth(lastMonth)
      to = endOfMonth(lastMonth)
    } else if (preset.value === "this_year") {
      from = startOfYear(today)
      to = today
    } else if (preset.value === "all_time") {
      from = null
      to = null
    }
    
    onDateRangeChange({ from, to })
    setOpen(false)
  }

  const getDisplayText = () => {
    if (!dateRange?.from && !dateRange?.to) {
      return "All Time"
    }
    if (dateRange?.from && dateRange?.to) {
      return `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`
    }
    if (dateRange?.from) {
      return `From ${format(dateRange.from, "MMM d, yyyy")}`
    }
    return "Select dates"
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal",
            !dateRange && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDisplayText()}
          <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex">
          {/* Presets sidebar */}
          <div className="border-r p-2 space-y-1">
            <p className="text-xs font-medium text-muted-foreground px-2 py-1">Quick Select</p>
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-left"
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          {/* Calendar */}
          <div className="p-3">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={(range) => {
                onDateRangeChange(range)
                if (range?.from && range?.to) {
                  setOpen(false)
                }
              }}
              numberOfMonths={2}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DateRangePicker
